import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Send, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AuraCitations, type Citation, type Confidence } from "@/components/AuraCitations";
import { supabase } from "@/integrations/supabase/client";

interface Message {
  role: "user" | "assistant";
  content: string;
  confidence?: Confidence;
  citations?: Citation[];
}

interface AuraChatProps {
  isOpen: boolean;
  onClose: () => void;
  userContext?: {
    symptoms?: string[];
    goals?: string[];
    anxietyLevel?: number;
  };
}

const AuraChat = ({ isOpen, onClose, userContext }: AuraChatProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Hi! I'm Aura, your wellness companion. How are you feeling today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const streamChat = async (userMessage: string) => {
    const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/aura-chat`;
    let assistantPlaceholderAdded = false;

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        throw new Error("Please sign in to chat with Aura.");
      }

      const { data: { user }, error: userError } = await supabase.auth.getUser(session.access_token);
      if (userError || !user) {
        await supabase.auth.signOut({ scope: "local" });
        throw new Error("Your session has expired. Please sign in again to chat with Aura.");
      }

      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          messages: [...messages, { role: "user", content: userMessage }],
          userContext,
        }),
      });

      if (!resp.ok) {
        let errorMessage = "Failed to get response";
        try {
          const error = await resp.json();
          errorMessage = error.error || errorMessage;
        } catch {
          errorMessage = await resp.text();
        }

        if (resp.status === 401) {
          await supabase.auth.signOut({ scope: "local" });
          throw new Error("Your session has expired. Please sign in again to chat with Aura.");
        }

        if (resp.status === 402) {
          throw new Error("Aura chat is available during free exploration, trial, or premium access.");
        }

        throw new Error(errorMessage || "Failed to get response");
      }

      if (!resp.body) throw new Error("No response body");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;
      let assistantMessage = "";

      // Add empty assistant message that we'll update
      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);
      assistantPlaceholderAdded = true;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            // Metadata prelude from Her Season aura-chat (confidence + citations)
            if (parsed.type === "hs_metadata") {
              setMessages((prev) => {
                const nm = [...prev];
                const last = nm[nm.length - 1];
                if (last && last.role === "assistant") {
                  last.confidence = parsed.confidence;
                  last.citations = parsed.citations;
                }
                return nm;
              });
              continue;
            }
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantMessage += content;
              // Strip inline confidence label and any HTML tags (e.g. <sup> citation markers) that the model may emit.
              const clean = assistantMessage
                .replace(/\[\[LABEL:(evidence_based|general_educational|seek_professional)\]\]/g, "")
                .replace(/<[^>]+>/g, "")
                .trim();
              setMessages((prev) => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1].content = clean;
                return newMessages;
              });
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message",
        variant: "destructive",
      });
      // Remove the empty assistant message on error
      if (assistantPlaceholderAdded) {
        setMessages((prev) => prev.slice(0, -1));
      }
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    await streamChat(userMessage);
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[60] flex items-end justify-end p-4">
      <div className="bg-card border rounded-2xl shadow-2xl w-full max-w-md h-[600px] flex flex-col overflow-hidden animate-in slide-in-from-bottom-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">Aura AI</h3>
              <p className="text-xs text-muted-foreground">Your wellness companion</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="hover:bg-primary/10 rounded-full"
            title="Close chat"
          >
            <X className="w-5 h-5 text-foreground" />
          </Button>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  {/* SECURITY: AI output MUST be rendered via React's escaped text nodes.
                      Never switch to dangerouslySetInnerHTML or an unsanitised HTML/Markdown
                      renderer here — model output is untrusted and could contain XSS payloads. */}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === "assistant" && msg.confidence && (
                    <AuraCitations confidence={msg.confidence} citations={msg.citations ?? []} />
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl px-4 py-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-primary/40 animate-pulse" />
                    <div className="w-2 h-2 rounded-full bg-primary/40 animate-pulse delay-75" />
                    <div className="w-2 h-2 rounded-full bg-primary/40 animate-pulse delay-150" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSend()}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1"
            />
            <Button onClick={handleSend} disabled={isLoading || !input.trim()} size="icon">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuraChat;
